import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import { useLang } from "../i18n";
import { Logo } from "./Header";
import { LanguageSelector } from "./LanguageSelector";
import { Seo } from "./Seo";
import { formatNewsDate, localizeNews } from "./News";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const T = {
  it: { back: "Torna alla home", label: "News", notFound: "News non trovata." },
  en: { back: "Back to home", label: "News", notFound: "News not found." },
};

export default function NewsDetailPage() {
  const { lang } = useLang();
  const s = T[lang];
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);
  const news = item ? localizeNews(item, lang) : null;

  useEffect(() => {
    axios.get(`${API}/news/${id}`).then((res) => setItem(res.data)).catch(() => setError(true));
    window.scrollTo(0, 0);
  }, [id]);

  return (
    <div className="min-h-screen bg-background" data-testid="news-detail-page">
      {news && (
        <Seo
          path={`/news/${id}`}
          title={`${news.title} — You4Mada`}
          description={news.excerpt || news.subtitle || "Storie e aggiornamenti dai progetti You4Mada in Madagascar."}
        />
      )}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#04122e]/70 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 md:px-8">
          <Logo onClick={() => navigate("/")} />
          <div className="flex items-center gap-4">
            <LanguageSelector compact />
            <button
              data-testid="news-back-home"
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-white/60 transition-colors hover:text-white"
            >
              <ArrowLeft size={14} />
              {s.back}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 pb-28 pt-32 md:px-8 md:pt-40">
        {error && (
          <p className="text-white/60" data-testid="news-detail-error">{s.notFound}</p>
        )}
        {news && (
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="text-[12px] font-semibold uppercase tracking-[0.35em] text-primary">
              {s.label} · {formatNewsDate(news.created_at, lang)}
            </p>
            <h1 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="news-detail-title">
              {news.title}
            </h1>
            <p className="mt-6 max-w-2xl font-serif text-xl italic leading-relaxed text-white/70">
              {news.excerpt}
            </p>
            {news.image_path && (
              <div className="mt-10 overflow-hidden rounded-3xl border border-white/10">
                <img
                  src={`${API}/news-images/${news.image_path}`}
                  alt={news.title}
                  className="max-h-[560px] w-full object-cover"
                  data-testid="news-detail-image"
                />
              </div>
            )}
            <div className="mt-10 space-y-5" data-testid="news-detail-content">
              {news.content.split("\n").filter(Boolean).map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-white/75 md:text-lg">
                  {p}
                </p>
              ))}
            </div>
          </motion.article>
        )}
      </main>
    </div>
  );
}
